import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartErrorEvent } from '@shopify/events';

/**
 * A custom element that adds or removes the "Shipping Protection" line item
 * from the cart in response to a single checkbox, mirroring the fetch +
 * section-morph pattern used by <cart-note> and <cart-discount-component>.
 */
class ShippingProtection extends Component {
  requiredRefs = ['protectionCheckbox'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /**
   * Adds or removes the shipping protection variant from the cart.
   * @param {Event} _event - The change event on the checkbox (unused; state is read from the ref).
   */
  toggleProtection = async (_event) => {
    const checkbox = this.refs.protectionCheckbox;
    if (!(checkbox instanceof HTMLInputElement)) return;

    const variantId = Number(this.dataset.variantId);
    const sectionId = this.dataset.sectionId;
    if (!variantId || !sectionId) return;

    if (this.#activeFetch) this.#activeFetch.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    const adding = checkbox.checked;
    this.setAttribute('data-loading', '');
    checkbox.disabled = true;

    try {
      const body = adding
        ? JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: [sectionId],
            sections_url: window.location.pathname,
          })
        : JSON.stringify({
            // /cart/change.js validates id as a digit string or a line-item key and
            // rejects a JSON number with 400; /cart/add.js accepts either.
            id: String(variantId),
            quantity: 0,
            sections: [sectionId],
            sections_url: window.location.pathname,
          });

      const response = await fetch(adding ? Theme.routes.cart_add_url : Theme.routes.cart_change_url, {
        ...fetchConfig('json', { body }),
        signal: abortController.signal,
      });

      const data = await response.json();

      if (!response.ok || data.errors) {
        checkbox.checked = !adding;
        this.dispatchEvent(
          new CartErrorEvent({
            error:
              (data.errors && (data.errors.items || data.errors)) ||
              data.description ||
              'Failed to update shipping protection',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
        return;
      }

      const sectionHtml = data.sections && data.sections[sectionId];
      if (sectionHtml) {
        morphSection(sectionId, sectionHtml, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error(error);
      checkbox.checked = !adding;
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Failed to update shipping protection',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      checkbox.disabled = false;
      this.removeAttribute('data-loading');
      this.#activeFetch = null;
    }
  };
}

if (!customElements.get('shipping-protection-component')) {
  customElements.define('shipping-protection-component', ShippingProtection);
}
